import { useCallback, useState } from 'react';
import {
  filterErrorsFromResponse,
  patchColumnFilters,
  readColumnFilters,
  sameColumnFilters,
} from '../components/game-data/columnFilters';
import type { ColumnFilterErrors } from '../components/game-data/columnFilters';
import type { ColumnFilterValues } from '../types/game-data.types';

export interface UseColumnFiltersResult {
  /** Применённые фильтры — уходят в GET списка. */
  values: ColumnFilterValues;
  /** Ошибки фильтров с бэка (400) — показываются под полем фильтра. */
  errors: ColumnFilterErrors | undefined;
  /**
   * Применить правку колонки. Вернёт новые фильтры или `null`, если ничего не поменялось —
   * тогда и перезагружать нечего. Ошибки по применённым параметрам снимаются: их проверит бэк заново.
   */
  apply: (patch: Readonly<Record<string, readonly string[]>>) => ColumnFilterValues | null;
  /** Сбросить все фильтры («Очистить»). */
  clear: () => void;
  /**
   * Разобрать ошибку загрузки списка: 400 по фильтрам ляжет под поля. `true` — ошибка про
   * фильтры и уже показана; `false` — чужая, показать её как обычно.
   */
  takeLoadError: (error: unknown) => boolean;
}

/**
 * Фильтры по колонкам таблицы игровых данных (docs/game-data.md §2d): state, ошибки с бэка
 * и начальное значение из адресной строки. Синхронизацию адреса страница ведёт сама — там же
 * сортировка и свои параметры.
 */
export function useColumnFilters(params: readonly string[], search: URLSearchParams): UseColumnFiltersResult {
  const [values, setValues] = useState<ColumnFilterValues>(() => readColumnFilters(search, params));
  const [errors, setErrors] = useState<ColumnFilterErrors | undefined>(undefined);

  const apply = useCallback(
    (patch: Readonly<Record<string, readonly string[]>>) => {
      const next = patchColumnFilters(values, patch);
      setErrors((prev) => {
        if (!prev) return prev;
        const rest = { ...prev };
        Object.keys(patch).forEach((param) => delete rest[param]);
        return Object.keys(rest).length > 0 ? rest : undefined;
      });
      if (sameColumnFilters(values, next)) return null;
      setValues(next);
      return next;
    },
    [values],
  );

  const clear = useCallback(() => {
    setValues({});
    setErrors(undefined);
  }, []);

  const takeLoadError = useCallback(
    (error: unknown) => {
      const found = filterErrorsFromResponse(error, params);
      if (!found) return false;
      setErrors(found);
      return true;
    },
    [params],
  );

  return { values, errors, apply, clear, takeLoadError };
}

export default useColumnFilters;
